import { useState, useEffect } from 'react';
import { Trophy, Star, Lock, Share2, Copy, Download, X, TrendingUp, Target, Zap, Crown, Flame, Globe, Sparkles, ExternalLink, Check, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

// ── Types ──
type TrophyTier = 'gold' | 'silver' | 'bronze';
type TrophyTab = 'earned' | 'all' | 'leaderboard';

interface TrophyItem {
  id: number;
  name: string;
  category: string;
  metric: string;
  metricValue: string;
  campaign: string;
  dateEarned: string;
  tier: TrophyTier;
  description: string;
  rankContext: string;
  locked: boolean;
  progress?: { label: string; current: number; target: number }[];
  unlockStatus?: 'close' | 'on-track' | 'locked';
}

// ── Mock Data ──
const earnedTrophies: TrophyItem[] = [
  { id: 1, name: 'Top CTR of the Month', category: 'Sweden — FMCG', metric: 'CTR', metricValue: '8.4%', campaign: 'Nike - Julkampanj', dateEarned: '15 Feb 2026', tier: 'gold', description: 'Awarded to the campaign with the highest click-through rate across all FMCG campaigns in Sweden this month.', rankContext: 'Top 1% of campaigns this month', locked: false },
  { id: 2, name: 'Best Engagement Rate', category: 'Nordics — Beauty', metric: 'Engagement', metricValue: '12.6%', campaign: 'Nike Grillkampanj', dateEarned: '2 Feb 2026', tier: 'gold', description: 'Highest engagement rate recorded among Nordic beauty campaigns.', rankContext: 'Top 3% of campaigns this quarter', locked: false },
  { id: 3, name: 'Most Viral Campaign', category: 'Sweden — All', metric: 'Shares', metricValue: '24.8K', campaign: 'Nike Ultrafärsk norsk lax', dateEarned: '28 Jan 2026', tier: 'silver', description: 'Campaign that achieved the highest share rate across all Swedish campaigns.', rankContext: 'Top 5% of campaigns', locked: false },
  { id: 4, name: 'TikTok Champion', category: 'Platform Awards', metric: 'Views', metricValue: '1.2M', campaign: 'Nike - Julkampanj', dateEarned: '20 Jan 2026', tier: 'silver', description: 'Best performing TikTok campaign of the month.', rankContext: 'Top 8% of TikTok campaigns', locked: false },
  { id: 5, name: 'Best Creator Partnership', category: 'Collaboration Awards', metric: 'ROI', metricValue: '4.2x', campaign: 'Nike Grillkampanj', dateEarned: '10 Jan 2026', tier: 'bronze', description: 'Most effective creator-brand collaboration this quarter.', rankContext: 'Top 10% of collaborations', locked: false },
  { id: 6, name: '1M Impressions Milestone', category: 'Milestone Awards', metric: 'Impressions', metricValue: '1.04M', campaign: 'Nike - Julkampanj', dateEarned: '5 Jan 2026', tier: 'bronze', description: 'Reached over 1 million impressions in a single campaign.', rankContext: 'Milestone achieved', locked: false },
];

const lockedTrophies: TrophyItem[] = [
  { id: 101, name: 'Most Viral Campaign of the Month', category: 'Viral Awards', metric: 'Reach', metricValue: '—', campaign: '—', dateEarned: '', tier: 'gold', description: 'Awarded to the campaign with the highest reach and share rate in a single month.', rankContext: '', locked: true, progress: [{ label: 'Reach', current: 320000, target: 500000 }, { label: 'Engagement Rate', current: 4.2, target: 3.0 }, { label: 'Share Rate', current: 2.1, target: 3.0 }], unlockStatus: 'close' },
  { id: 102, name: 'Influencer Marketing Champion', category: 'Growth Awards', metric: 'Trophies', metricValue: '—', campaign: '—', dateEarned: '', tier: 'gold', description: 'Earn 15 trophies to reach the highest level of recognition.', rankContext: '', locked: true, progress: [{ label: 'Trophies Earned', current: 6, target: 15 }], unlockStatus: 'on-track' },
  { id: 103, name: 'YouTube Impact Award', category: 'Platform Awards', metric: 'Watch Time', metricValue: '—', campaign: '—', dateEarned: '', tier: 'silver', description: 'Achieve top watch time on YouTube campaigns.', rankContext: '', locked: true, progress: [{ label: 'Watch Hours', current: 820, target: 2000 }, { label: 'Completion Rate', current: 45, target: 60 }], unlockStatus: 'locked' },
  { id: 104, name: 'Best Storytelling Campaign', category: 'Creative Awards', metric: 'Sentiment', metricValue: '—', campaign: '—', dateEarned: '', tier: 'silver', description: 'Campaign with highest positive sentiment and story completion.', rankContext: '', locked: true, progress: [{ label: 'Positive Sentiment', current: 72, target: 85 }, { label: 'Story Completion', current: 58, target: 75 }], unlockStatus: 'locked' },
  { id: 105, name: 'Top Nordic Campaign', category: 'Market Awards', metric: 'Overall Score', metricValue: '—', campaign: '—', dateEarned: '', tier: 'gold', description: 'Best performing campaign across all Nordic markets.', rankContext: '', locked: true, progress: [{ label: 'Reach (Nordic)', current: 450000, target: 1000000 }, { label: 'Avg Engagement', current: 5.1, target: 7.0 }], unlockStatus: 'on-track' },
  { id: 106, name: '100 Influencers Activated', category: 'Milestone Awards', metric: 'Creators', metricValue: '—', campaign: '—', dateEarned: '', tier: 'bronze', description: 'Activate 100 unique influencers across all campaigns.', rankContext: '', locked: true, progress: [{ label: 'Influencers Activated', current: 34, target: 100 }], unlockStatus: 'locked' },
];

const leaderboard = [
  { rank: 1, name: 'Nike', trophies: 6, level: 'Viral Strategist', avatar: '🏆' },
  { rank: 2, name: 'Adidas Nordic', trophies: 5, level: 'Campaign Optimizer', avatar: '🥈' },
  { rank: 3, name: 'H&M Sweden', trophies: 4, level: 'Campaign Optimizer', avatar: '🥉' },
  { rank: 4, name: 'IKEA', trophies: 3, level: 'Influencer Starter', avatar: '4' },
  { rank: 5, name: 'Spotify', trophies: 3, level: 'Influencer Starter', avatar: '5' },
];

const levels = [
  { level: 1, name: 'Influencer Starter', minTrophies: 0 },
  { level: 2, name: 'Campaign Optimizer', minTrophies: 4 },
  { level: 3, name: 'Viral Strategist', minTrophies: 8 },
  { level: 4, name: 'Performance Leader', minTrophies: 12 },
  { level: 5, name: 'Influencer Marketing Champion', minTrophies: 15 },
];

// ── Helpers ──
const tierColors: Record<TrophyTier, { bg: string; border: string; glow: string; icon: string; text: string; badge: string }> = {
  gold:   { bg: 'bg-amber-50', border: 'border-amber-200/60', glow: 'shadow-[0_0_24px_rgba(245,158,11,0.25)]', icon: 'text-amber-500', text: 'text-amber-700', badge: 'bg-gradient-to-br from-amber-400 to-yellow-500' },
  silver: { bg: 'bg-slate-50', border: 'border-slate-200/60', glow: 'shadow-[0_0_24px_rgba(148,163,184,0.3)]', icon: 'text-slate-400', text: 'text-slate-600', badge: 'bg-gradient-to-br from-slate-300 to-slate-400' },
  bronze: { bg: 'bg-orange-50', border: 'border-orange-200/60', glow: 'shadow-[0_0_24px_rgba(234,88,12,0.2)]', icon: 'text-orange-400', text: 'text-orange-700', badge: 'bg-gradient-to-br from-orange-300 to-orange-500' },
};

const TrophyBadge = ({ tier, size = 'md', locked = false }: { tier: TrophyTier; size?: 'sm' | 'md' | 'lg'; locked?: boolean }) => {
  const sizeMap = { sm: 'w-10 h-10', md: 'w-14 h-14', lg: 'w-24 h-24' };
  const iconMap = { sm: 'w-5 h-5', md: 'w-7 h-7', lg: 'w-12 h-12' };
  const tc = tierColors[tier];
  if (locked) {
    return (
      <div className={`${sizeMap[size]} rounded-2xl bg-gray-100 flex items-center justify-center`}>
        <Lock className={`${iconMap[size]} text-gray-300`} />
      </div>
    );
  }
  return (
    <div className={`${sizeMap[size]} rounded-2xl ${tc.badge} flex items-center justify-center shadow-sm`}>
      <Trophy className={`${iconMap[size]} text-white drop-shadow-sm`} />
    </div>
  );
};

const formatNum = (n: number) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return n.toString();
};

const unlockStatusConfig: Record<string, { label: string; color: string; bg: string }> = {
  close: { label: 'Close to unlocking', color: 'text-amber-600', bg: 'bg-amber-50' },
  'on-track': { label: 'On track', color: 'text-[#be43ff]', bg: 'bg-[#be43ff]/8' },
  locked: { label: 'Locked', color: 'text-gray-400', bg: 'bg-gray-50' },
};

// ── Component ──
const allTrophiesList = [...earnedTrophies, ...lockedTrophies];

export function TrophyRoomPage({
  trophyId: trophyIdFromUrl,
  onNavigate: _onNavigate,
  navigate,
}: {
  trophyId?: string;
  onNavigate?: (page: string) => void;
  navigate?: (path: string) => void;
} = {}) {
  const [activeTab, setActiveTab] = useState<TrophyTab>('earned');
  const [selectedTrophy, setSelectedTrophy] = useState<TrophyItem | null>(null);
  const [showSharePreview, setShowSharePreview] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const currentLevel = levels.reduce((acc, l) => (earnedTrophies.length >= l.minTrophies ? l : acc), levels[0]);
  const nextLevel = levels.find(l => l.minTrophies > earnedTrophies.length) || levels[levels.length - 1];
  const levelProgress = nextLevel.minTrophies > 0 ? Math.min(100, (earnedTrophies.length / nextLevel.minTrophies) * 100) : 100;

  // Sync selected trophy from URL (/awards/:trophyId)
  useEffect(() => {
    if (trophyIdFromUrl) {
      const trophy = allTrophiesList.find((t) => String(t.id) === trophyIdFromUrl);
      setSelectedTrophy(trophy ?? null);
    } else {
      setSelectedTrophy(null);
    }
  }, [trophyIdFromUrl]);

  const handleCopyText = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openDetail = (trophy: TrophyItem) => {
    setSelectedTrophy(trophy);
    if (navigate) navigate(`/awards/${trophy.id}`);
    if (!trophy.locked) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2000);
    }
  };

  const closeDetail = () => {
    setSelectedTrophy(null);
    if (navigate) navigate('/awards');
  };

  const allTrophies = allTrophiesList;

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <div className="max-w-[1400px] mx-auto p-6 lg:p-8 space-y-6">

        {/* ── Hero Banner ── */}
        <div className="bg-gradient-to-r from-[#2e024c] via-[#4a1278] to-[#5a1a8a] rounded-[24px] overflow-hidden relative">
          <div className="absolute inset-0 opacity-15" style={{
            backgroundImage: 'radial-gradient(circle at 75% 25%, #be43ff 0%, transparent 40%), radial-gradient(circle at 25% 75%, #a9f600 0%, transparent 40%), radial-gradient(circle at 50% 50%, #fbbf24 0%, transparent 30%)'
          }} />
          <div className="relative z-10 p-8 lg:p-10">
            <div className="flex items-start justify-between">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full w-fit mb-4">
                  <Trophy className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-white/80 text-[11px]" style={{ fontWeight: 600 }}>Trophy Room</span>
                </div>
                <h1 className="text-white text-[28px] lg:text-[36px] mb-2" style={{ fontWeight: 700 }}>
                  Your Achievements
                </h1>
                <p className="text-white/50 text-sm max-w-lg">
                  Celebrate your best-performing campaigns and industry achievements. Share your wins with the world.
                </p>
              </div>
              {/* Level Badge */}
              <div className="shrink-0 bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center min-w-[180px]">
                <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center mb-3 shadow-lg shadow-amber-500/20">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div className="text-[10px] text-white/40 mb-1" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Level {currentLevel.level}</div>
                <div className="text-white text-[14px] mb-3" style={{ fontWeight: 700 }}>{currentLevel.name}</div>
                <div className="w-full bg-white/10 rounded-full h-1.5 mb-1.5">
                  <div className="bg-[#a9f600] h-1.5 rounded-full transition-all" style={{ width: `${levelProgress}%` }} />
                </div>
                <div className="text-[10px] text-white/40">
                  {nextLevel.minTrophies - earnedTrophies.length} more to <span className="text-[#a9f600]">{nextLevel.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Quick Stats ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Trophies', value: earnedTrophies.length.toString(), icon: Trophy, color: 'text-amber-500', iconBg: 'bg-amber-50' },
            { label: "This Month's Awards", value: '2', icon: Star, color: 'text-[#be43ff]', iconBg: 'bg-[#be43ff]/8' },
            { label: 'Ranking Percentile', value: 'Top 3%', icon: TrendingUp, color: 'text-emerald-600', iconBg: 'bg-emerald-50' },
            { label: 'Best Market', value: 'Sweden', icon: Globe, color: 'text-blue-600', iconBg: 'bg-blue-50' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-[20px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5 flex items-center gap-4">
              <div className={`w-11 h-11 rounded-xl ${stat.iconBg} flex items-center justify-center shrink-0`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div>
                <span className="text-[9px] text-[#98a2b3] block" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{stat.label}</span>
                <span className="text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>{stat.value}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Tab Bar ── */}
        <div className="flex items-center gap-2">
          {([
            { id: 'earned' as TrophyTab, label: 'Earned', count: earnedTrophies.length },
            { id: 'all' as TrophyTab, label: 'All Trophies', count: allTrophies.length },
            { id: 'leaderboard' as TrophyTab, label: 'Leaderboard', count: null },
          ]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] transition-all ${
                activeTab === tab.id
                  ? 'bg-[#101828] text-white'
                  : 'text-[#667085] hover:bg-white hover:shadow-sm'
              }`}
              style={{ fontWeight: 600 }}
            >
              {tab.label}
              {tab.count !== null && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${
                  activeTab === tab.id ? 'bg-white/15' : 'bg-[#f8f9fa]'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Earned Tab ── */}
        {activeTab === 'earned' && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {earnedTrophies.map((trophy) => {
              const tc = tierColors[trophy.tier];
              return (
                <div
                  key={trophy.id}
                  onClick={() => openDetail(trophy)}
                  className={`group bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:${tc.glow} border border-transparent hover:${tc.border} p-6 cursor-pointer transition-all duration-300`}
                  style={{ transition: 'box-shadow 0.3s, border-color 0.3s' }}
                >
                  <div className="flex items-start justify-between mb-5">
                    <TrophyBadge tier={trophy.tier} />
                    <span className={`text-[9px] px-2 py-1 rounded-lg ${tc.bg} ${tc.text}`} style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
                      {trophy.tier}
                    </span>
                  </div>
                  <h3 className="text-[15px] text-[#101828] mb-1" style={{ fontWeight: 700 }}>{trophy.name}</h3>
                  <p className="text-[11px] text-[#98a2b3] mb-4" style={{ fontWeight: 500 }}>{trophy.category}</p>

                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#f8f9fa] text-[11px] text-[#667085]">
                      <Target className="w-3 h-3 text-[#98a2b3]" />
                      {trophy.metric}: <span className="text-[#101828]" style={{ fontWeight: 700 }}>{trophy.metricValue}</span>
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                    <div>
                      <div className="text-[10px] text-[#98a2b3] mb-0.5" style={{ fontWeight: 500 }}>{trophy.campaign}</div>
                      <div className="text-[10px] text-[#d0d5dd]">{trophy.dateEarned}</div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="w-7 h-7 rounded-lg bg-[#f8f9fa] flex items-center justify-center hover:bg-gray-100 transition-colors" title="View Details">
                        <Eye className="w-3.5 h-3.5 text-[#667085]" />
                      </button>
                      <button className="w-7 h-7 rounded-lg bg-[#0077b5]/10 flex items-center justify-center hover:bg-[#0077b5]/15 transition-colors" title="Share on LinkedIn">
                        <Share2 className="w-3.5 h-3.5 text-[#0077b5]" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── All Trophies Tab ── */}
        {activeTab === 'all' && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {allTrophies.map((trophy) => {
              const tc = tierColors[trophy.tier];
              if (!trophy.locked) {
                return (
                  <div
                    key={trophy.id}
                    onClick={() => openDetail(trophy)}
                    className="group bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-6 cursor-pointer transition-all duration-300 hover:shadow-md"
                  >
                    <div className="flex items-start justify-between mb-5">
                      <TrophyBadge tier={trophy.tier} />
                      <span className={`text-[9px] px-2 py-1 rounded-lg ${tc.bg} ${tc.text}`} style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
                        {trophy.tier}
                      </span>
                    </div>
                    <h3 className="text-[15px] text-[#101828] mb-1" style={{ fontWeight: 700 }}>{trophy.name}</h3>
                    <p className="text-[11px] text-[#98a2b3]">{trophy.category}</p>
                    <div className="mt-3 text-[10px] text-[#d0d5dd]">{trophy.dateEarned}</div>
                  </div>
                );
              }
              // Locked trophy card
              const usCfg = unlockStatusConfig[trophy.unlockStatus || 'locked'];
              return (
                <div
                  key={trophy.id}
                  onClick={() => openDetail(trophy)}
                  className="group bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-6 cursor-pointer transition-all duration-300 hover:shadow-md opacity-70 hover:opacity-100"
                >
                  <div className="flex items-start justify-between mb-5">
                    <TrophyBadge tier={trophy.tier} locked />
                    <span className={`text-[9px] px-2 py-1 rounded-lg ${usCfg.bg} ${usCfg.color}`} style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
                      {usCfg.label}
                    </span>
                  </div>
                  <h3 className="text-[15px] text-[#101828] mb-1" style={{ fontWeight: 700 }}>{trophy.name}</h3>
                  <p className="text-[11px] text-[#98a2b3] mb-4">{trophy.category}</p>
                  {/* Progress bars */}
                  {trophy.progress && (
                    <div className="space-y-2.5">
                      {trophy.progress.map((p, idx) => {
                        const pct = Math.min(100, (p.current / p.target) * 100);
                        const done = pct >= 100;
                        return (
                          <div key={idx}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-[#667085]" style={{ fontWeight: 500 }}>{p.label}</span>
                              <span className="text-[10px] text-[#101828]" style={{ fontWeight: 600 }}>
                                {typeof p.current === 'number' && p.current >= 1000 ? formatNum(p.current) : p.current}
                                {typeof p.target === 'number' && p.target >= 100 ? ` / ${formatNum(p.target)}` : ` / ${p.target}`}
                                {typeof p.target === 'number' && p.target < 100 && '%'}
                              </span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full transition-all ${done ? 'bg-emerald-500' : 'bg-[#be43ff]'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Leaderboard Tab ── */}
        {activeTab === 'leaderboard' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Leaderboard Card */}
            <div className="lg:col-span-2 bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
              <div className="px-7 pt-7 pb-4 flex items-center justify-between">
                <h3 className="text-[18px] text-[#101828]" style={{ fontWeight: 700 }}>Top Brands This Month</h3>
                <span className="text-[10px] text-[#98a2b3] bg-[#f8f9fa] px-2.5 py-1 rounded-lg" style={{ fontWeight: 600 }}>Sweden</span>
              </div>
              <div className="px-4 pb-4 space-y-1.5">
                {leaderboard.map((entry) => (
                  <div key={entry.rank} className={`flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-colors ${entry.rank === 1 ? 'bg-amber-50/50' : 'hover:bg-[#f8f9fa]/70'}`}>
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-[13px] shrink-0 ${
                      entry.rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white' :
                      entry.rank === 2 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-white' :
                      entry.rank === 3 ? 'bg-gradient-to-br from-orange-300 to-orange-500 text-white' :
                      'bg-[#f8f9fa] text-[#98a2b3]'
                    }`} style={{ fontWeight: 700 }}>
                      {entry.rank <= 3 ? entry.avatar : entry.rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{entry.name}</div>
                      <div className="text-[10px] text-[#98a2b3]">{entry.level}</div>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#f8f9fa]">
                      <Trophy className="w-3 h-3 text-amber-400" />
                      <span className="text-[12px] text-[#101828]" style={{ fontWeight: 700 }}>{entry.trophies}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Milestone Card */}
            <div className="space-y-5">
              <div className="bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-7">
                <h3 className="text-[16px] text-[#101828] mb-5" style={{ fontWeight: 700 }}>Next Milestone</h3>
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#be43ff] to-[#8b2cc4] flex items-center justify-center shrink-0">
                    <Zap className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="text-[13px] text-[#101828] mb-0.5" style={{ fontWeight: 700 }}>Level {nextLevel.level}</div>
                    <div className="text-[11px] text-[#667085]">{nextLevel.name}</div>
                  </div>
                </div>
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 600 }}>Progress</span>
                    <span className="text-[10px] text-[#101828]" style={{ fontWeight: 700 }}>{earnedTrophies.length} / {nextLevel.minTrophies}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="bg-gradient-to-r from-[#be43ff] to-[#a9f600] h-2 rounded-full transition-all" style={{ width: `${levelProgress}%` }} />
                  </div>
                </div>
                <p className="text-[11px] text-[#667085] mt-3">
                  Earn <span className="text-[#be43ff]" style={{ fontWeight: 700 }}>{nextLevel.minTrophies - earnedTrophies.length} more trophies</span> to reach {nextLevel.name}
                </p>
              </div>

              {/* Achievement Levels */}
              <div className="bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-7">
                <h3 className="text-[16px] text-[#101828] mb-5" style={{ fontWeight: 700 }}>All Levels</h3>
                <div className="space-y-2.5">
                  {levels.map((lvl) => {
                    const unlocked = earnedTrophies.length >= lvl.minTrophies;
                    return (
                      <div key={lvl.level} className={`flex items-center gap-3 p-3 rounded-xl ${unlocked ? 'bg-[#be43ff]/5' : 'bg-[#f8f9fa]'}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[11px] ${
                          unlocked ? 'bg-[#be43ff] text-white' : 'bg-gray-200 text-gray-400'
                        }`} style={{ fontWeight: 700 }}>
                          {lvl.level}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-[12px] ${unlocked ? 'text-[#101828]' : 'text-[#98a2b3]'}`} style={{ fontWeight: 600 }}>{lvl.name}</div>
                          <div className="text-[10px] text-[#d0d5dd]">{lvl.minTrophies} trophies</div>
                        </div>
                        {unlocked && <Check className="w-4 h-4 text-[#be43ff]" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/*  TROPHY DETAIL MODAL                       */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatePresence>
        {selectedTrophy && !showSharePreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
            onClick={closeDetail}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-[28px] shadow-2xl max-w-lg w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative bg-gradient-to-r from-[#2e024c] to-[#5a1a8a] p-8 text-center">
                <div className="absolute inset-0 opacity-10" style={{
                  backgroundImage: 'radial-gradient(circle at 50% 50%, #fbbf24 0%, transparent 50%)'
                }} />
                <button
                  onClick={closeDetail}
                  className="absolute top-4 right-4 w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
                <div className="relative z-10">
                  <TrophyBadge tier={selectedTrophy.tier} size="lg" locked={selectedTrophy.locked} />
                  <h2 className="text-white text-[20px] mt-5 mb-1" style={{ fontWeight: 700 }}>{selectedTrophy.name}</h2>
                  <p className="text-white/50 text-[12px]">{selectedTrophy.category}</p>
                </div>

                {/* Confetti */}
                {showConfetti && !selectedTrophy.locked && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    {[...Array(20)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ y: -20, x: Math.random() * 400 - 200, opacity: 1, rotate: 0 }}
                        animate={{ y: 300, opacity: 0, rotate: Math.random() * 360 }}
                        transition={{ duration: 1.5 + Math.random(), delay: Math.random() * 0.5 }}
                        className="absolute top-0 left-1/2 w-2 h-2 rounded-full"
                        style={{ background: ['#fbbf24', '#be43ff', '#a9f600', '#ec4899', '#3b82f6'][i % 5] }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Modal Body */}
              <div className="p-7">
                <p className="text-[13px] text-[#667085] mb-5 leading-relaxed">{selectedTrophy.description}</p>

                {!selectedTrophy.locked ? (
                  <>
                    {/* Earned info */}
                    <div className="grid grid-cols-2 gap-3 mb-5">
                      <div className="bg-[#f8f9fa] rounded-xl p-3.5">
                        <div className="text-[9px] text-[#98a2b3] mb-1" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Campaign</div>
                        <div className="text-[12px] text-[#101828]" style={{ fontWeight: 600 }}>{selectedTrophy.campaign}</div>
                      </div>
                      <div className="bg-[#f8f9fa] rounded-xl p-3.5">
                        <div className="text-[9px] text-[#98a2b3] mb-1" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{selectedTrophy.metric}</div>
                        <div className="text-[20px] text-[#101828]" style={{ fontWeight: 700 }}>{selectedTrophy.metricValue}</div>
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-[11px] mb-6" style={{ fontWeight: 600 }}>
                      <TrendingUp className="w-3.5 h-3.5" />
                      {selectedTrophy.rankContext}
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowSharePreview(true)}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#0077b5] text-white text-[12px] hover:bg-[#006399] active:scale-[0.97] transition-all"
                        style={{ fontWeight: 600 }}
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        Share on LinkedIn
                      </button>
                      <button className="w-10 h-10 rounded-xl bg-[#f8f9fa] flex items-center justify-center hover:bg-gray-100 transition-colors" title="Copy image">
                        <Copy className="w-4 h-4 text-[#667085]" />
                      </button>
                      <button className="w-10 h-10 rounded-xl bg-[#f8f9fa] flex items-center justify-center hover:bg-gray-100 transition-colors" title="Download badge">
                        <Download className="w-4 h-4 text-[#667085]" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Requirements */}
                    <div className="space-y-3 mb-5">
                      {selectedTrophy.progress?.map((p, idx) => {
                        const pct = Math.min(100, (p.current / p.target) * 100);
                        const done = pct >= 100;
                        return (
                          <div key={idx} className="bg-[#f8f9fa] rounded-xl p-3.5">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[11px] text-[#667085]" style={{ fontWeight: 500 }}>{p.label}</span>
                              <span className={`text-[11px] ${done ? 'text-emerald-600' : 'text-[#101828]'}`} style={{ fontWeight: 600 }}>
                                {typeof p.current === 'number' && p.current >= 1000 ? formatNum(p.current) : p.current}
                                {typeof p.target === 'number' && p.target >= 100 ? ` / ${formatNum(p.target)}` : ` / ${p.target}`}
                                {typeof p.target === 'number' && p.target < 100 && '%'}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200/60 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full ${done ? 'bg-emerald-500' : 'bg-[#be43ff]'}`} style={{ width: `${pct}%` }} />
                            </div>
                            {done && <div className="text-[10px] text-emerald-500 mt-1" style={{ fontWeight: 600 }}>Requirement met!</div>}
                          </div>
                        );
                      })}
                    </div>
                    {selectedTrophy.unlockStatus && (
                      <div className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-[11px] ${unlockStatusConfig[selectedTrophy.unlockStatus].bg} ${unlockStatusConfig[selectedTrophy.unlockStatus].color}`} style={{ fontWeight: 600 }}>
                        {selectedTrophy.unlockStatus === 'close' && <Flame className="w-3.5 h-3.5" />}
                        {selectedTrophy.unlockStatus === 'on-track' && <TrendingUp className="w-3.5 h-3.5" />}
                        {selectedTrophy.unlockStatus === 'locked' && <Lock className="w-3.5 h-3.5" />}
                        {unlockStatusConfig[selectedTrophy.unlockStatus].label}
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════ */}
      {/*  LINKEDIN SHARE PREVIEW MODAL              */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatePresence>
        {showSharePreview && selectedTrophy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
            onClick={() => { setShowSharePreview(false); closeDetail(); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-[28px] shadow-2xl max-w-xl w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-7">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-[18px] text-[#101828]" style={{ fontWeight: 700 }}>Share on LinkedIn</h3>
                  <button
                    onClick={() => { setShowSharePreview(false); closeDetail(); }}
                    className="w-8 h-8 rounded-xl bg-[#f8f9fa] flex items-center justify-center hover:bg-gray-100"
                  >
                    <X className="w-4 h-4 text-[#667085]" />
                  </button>
                </div>

                {/* LinkedIn Preview Card */}
                <div className="border border-gray-200 rounded-2xl overflow-hidden mb-5">
                  {/* Card header — mimics LinkedIn post card */}
                  <div className="bg-gradient-to-r from-[#2e024c] to-[#5a1a8a] p-8 text-center relative">
                    <div className="absolute inset-0 opacity-10" style={{
                      backgroundImage: 'radial-gradient(circle at 50% 50%, #fbbf24 0%, transparent 50%)'
                    }} />
                    <div className="relative z-10">
                      <TrophyBadge tier={selectedTrophy.tier} size="md" />
                      <h4 className="text-white text-[16px] mt-4 mb-1" style={{ fontWeight: 700 }}>{selectedTrophy.name}</h4>
                      <p className="text-white/50 text-[11px]">{selectedTrophy.category}</p>
                      <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg mt-3">
                        <span className="text-white text-[13px]" style={{ fontWeight: 700 }}>{selectedTrophy.metricValue}</span>
                        <span className="text-white/50 text-[10px]">{selectedTrophy.metric}</span>
                      </div>
                    </div>
                  </div>
                  <div className="px-5 py-4 bg-gray-50/50">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md bg-[#be43ff] flex items-center justify-center">
                        <Sparkles className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-[11px] text-[#667085]">Built with <span className="text-[#101828]" style={{ fontWeight: 600 }}>Beatly Ad Studios</span></span>
                    </div>
                  </div>
                </div>

                {/* Text preview */}
                <div className="bg-[#f8f9fa] rounded-xl p-4 mb-5">
                  <p className="text-[12px] text-[#344054] leading-relaxed" style={{ fontWeight: 500 }}>
                    Excited to share that our campaign won <span style={{ fontWeight: 700 }}>{selectedTrophy.name}</span> in {selectedTrophy.category} on Beatly's Ad Studios platform 🏆
                    <br /><br />
                    Influencer marketing continues to outperform expectations. Our {selectedTrophy.metric.toLowerCase()} reached <span style={{ fontWeight: 700 }}>{selectedTrophy.metricValue}</span> — placing us in the <span style={{ fontWeight: 700 }}>{selectedTrophy.rankContext}</span>.
                    <br /><br />
                    #InfluencerMarketing #BeatlyAdStudios #CreatorEconomy
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#0077b5] text-white text-[12px] hover:bg-[#006399] active:scale-[0.97] transition-all"
                    style={{ fontWeight: 600 }}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Share to LinkedIn
                  </button>
                  <button
                    onClick={handleCopyText}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#f8f9fa] text-[12px] text-[#101828] hover:bg-gray-100 active:scale-[0.97] transition-all"
                    style={{ fontWeight: 600 }}
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied!' : 'Copy Text'}
                  </button>
                  <button className="w-10 h-10 rounded-xl bg-[#f8f9fa] flex items-center justify-center hover:bg-gray-100 transition-colors" title="Download graphic">
                    <Download className="w-4 h-4 text-[#667085]" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
